// @ts-check
import ZeroDataGrid from '~/components/Shared/ZeroDataGrid/ZeroDataGrid';
import { useDataGridSetup } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';
import useOrganizationId from '~/hooks/useOrganizationId';
import useZeroSelector from '~/hooks/useZeroSelector';
import { objectIsEmpty, setGridLoading } from '~/other/Helper';
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIncidentTemplateDataGridContext } from '~/components/Incidents/IncidentTemplateDataGrid/IncidentTemplateDataGrid.context';
import { IncidentTemplateDataGridModals } from '~/components/Incidents/IncidentTemplateDataGrid/IncidentTemplateDataGrid.modals';
import { EventNames, getColumnDefs, getSortByColumnName, loadIncidentTemplates } from '~/components/Incidents/IncidentTemplateDataGrid/IncidentTemplateDataGrid.utils';

const TEMPLATES_PER_PAGE = 50;

function useIncidentTemplateDataGridQuery() {
    const formsQuery = useZeroSelector(state => state.forms.query);
    const [query, setQuery] = useState({ formsQuery });

    useEffect(() => {
        setQuery({ formsQuery });
    }, [formsQuery]);

    return query;
}

/**
 * @param {{
 *  showCreateModal: boolean,
 *  onCloseCreateModal: () => void;
 * }} props
 */
export default function IncidentTemplateDataGrid({ showCreateModal, onCloseCreateModal }) {
    const location = useLocation();
    const { gridRef, settings, setSettings, rowCount } = useIncidentTemplateDataGridContext();
    const query = useIncidentTemplateDataGridQuery();
    const incidentFilterQuery = useZeroSelector(state => state.incidents.filterQuery);

    const colDefs = useMemo(() => getColumnDefs(), []);

    const getRows = useCallback(async (/** @type {AgGrid.IGetRowsParams} */params) => {
        const perPage = TEMPLATES_PER_PAGE;
        const currentPage = Math.floor((params.startRow ?? 0) / perPage) + 1;

        const { formsQuery } = query;

        const queryParams = new URLSearchParams(formsQuery);
        queryParams.set('team_uuid', 'my_teams');
        queryParams.set('all', 'true');
        queryParams.set('form_types', 'incident');
        queryParams.set('include_fields', 'false');
        queryParams.set('exclude_restricted', 'true');
        queryParams.set('paginate', 'true');
        queryParams.set('page', String(currentPage));
        queryParams.set('per_page', String(perPage));

        const incidentFilterParams = new URLSearchParams(incidentFilterQuery);
        if (incidentFilterParams.has('search')) {
            queryParams.set('search', incidentFilterParams.get('search'));
        }

        if (!objectIsEmpty(params.filterModel)) {
            queryParams.set('ag_filter', JSON.stringify(params.filterModel));
        }

        params.sortModel.forEach(sortModel => {
            let col = getSortByColumnName(sortModel.colId);
            if (sortModel.sort === 'desc') {
                col = `-${col}`;
            }
            queryParams.append('sort_by', col);
        });

        const gridApi = gridRef.current?.api;
        if (rowCount === null) {
            setGridLoading(gridApi, true);
        }
        
        const { forms, totalForms } = await loadIncidentTemplates(queryParams);

        params.successCallback(forms, totalForms);
        if (rowCount === null) {
            setGridLoading(gridApi, false);
        }
    }, [rowCount, query, incidentFilterQuery]);

    const clearTemplateCache = useCallback(() => {
        loadIncidentTemplates.cache.clear();
    }, []);

    const { onGridReady, onStateUpdated } = useDataGridSetup({
        eventNames: EventNames,
        autoLoadData: true,
        gridRef,
        colDefs,
        getRows,
        setSettings,
        onReset: clearTemplateCache,
        onRefresh: clearTemplateCache,
    });
    
    useEffect(() => {
        // Clear memoization when component is unmounted
        return () => {
            clearTemplateCache();
        }
    }, [clearTemplateCache]);

    return (
        <>
            <ZeroDataGrid
                gridRef={gridRef}
                columnDefs={colDefs}
                defaultColDef={{
                    cellStyle: {
                        display: 'flex',
                        alignItems: 'center',
                    }
                }}
                initialState={location.state?.gridState ?? settings}
                onGridReady={onGridReady}
                onStateUpdated={onStateUpdated}
                cacheBlockSize={TEMPLATES_PER_PAGE}
                cacheOverflowSize={2}
                maxConcurrentDatasourceRequests={2}
                infiniteInitialRowCount={rowCount ?? TEMPLATES_PER_PAGE}
                pagination={true}
                paginationPageSizeSelector={false}
                paginationPageSize={rowCount ?? TEMPLATES_PER_PAGE}
                tooltipShowDelay={500}
                tooltipShowMode="standard"
                suppressCellFocus
            />
            <IncidentTemplateDataGridModals 
                showCreateModal={showCreateModal} 
                onCloseCreateModal={onCloseCreateModal}
            />
        </>
    );
}
